/**
 * Make a non-button element that behaves like one actually behave like one (#1250).
 *
 * The codebase had fourteen elements styled and wired as interactive, given
 * `role="button"` or `role="link"`, and carrying neither `tabIndex` nor a key
 * handler. That combination is the worst of both: a screen reader announces a
 * control that can be activated, and the keyboard cannot reach it. In the
 * applications pane it meant a keyboard user could decide the FIRST applicant
 * and no other — every other control in the row was focusable, but the row that
 * selects WHICH applicant is under review was not.
 *
 * Spreading this instead of hand-writing the three props at each site is the
 * point: the next one is correct by default, and the omission cannot come back
 * one element at a time.
 *
 * Native `<button>`/`<a href>` remain the better answer where the layout allows
 * one — this is for the places already committed to a div or a span.
 */

import type { KeyboardEvent, MouseEvent } from 'react'

/** What the callback receives — a click, or the Enter/Space that stands in for one. */
export type ActivateEvent = MouseEvent | KeyboardEvent

export interface ActivatableProps {
  tabIndex: 0
  onClick: (e: MouseEvent) => void
  onKeyDown: (e: KeyboardEvent) => void
}

export interface ActivatableOptions {
  /**
   * Stop the event reaching an activatable ancestor.
   *
   * Needed wherever one of these sits inside another — an applicant's name
   * inside the row that selects that applicant. Without it, opening the profile
   * would also change which applicant is being reviewed.
   */
  nested?: boolean
}

export function activatable(
  onActivate: (e: ActivateEvent) => void,
  { nested = false }: ActivatableOptions = {},
): ActivatableProps {
  return {
    tabIndex: 0,
    onClick: (e: MouseEvent) => {
      if (nested) e.stopPropagation()
      onActivate(e)
    },
    onKeyDown: (e: KeyboardEvent) => {
      // Enter and Space are what a real button answers to. Space is also the
      // page-scroll key, so it must be prevented once it has been claimed.
      if (e.key !== 'Enter' && e.key !== ' ') return
      // Only answer for ourselves. Several of these wrap a card or row that
      // contains real buttons, and a keydown on a nested button bubbles here —
      // preventDefault() on it would cancel the browser's own Enter-activates-
      // the-button behaviour, so the inner control would go dead.
      if (e.target !== e.currentTarget) return
      if (nested) e.stopPropagation()
      e.preventDefault()
      onActivate(e)
    },
  }
}
